import {
  Sidebar,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarSpace,
  SidebarSubmenu,
  SidebarSubmenuItem,
} from '@backstage/core-components';
import {
  NavContentBlueprint,
  type NavContentNavItems,
} from '@backstage/plugin-app-react';
import { SidebarLogo } from './SidebarLogo';
import { useIsDevopsAdmin } from '../migration';
import MenuIcon from '@material-ui/icons/Menu';
import HomeIcon from '@material-ui/icons/Home';
import SearchIcon from '@material-ui/icons/Search';
import CategoryIcon from '@material-ui/icons/Category';
import { WorkflowSidebarItem } from './WorkflowSidebarItem';
import GitHubIcon from '@material-ui/icons/GitHub';
import { SidebarSearchModal } from '@backstage/plugin-search';
import { UserSettingsSignInAvatar } from '@backstage/plugin-user-settings';
import { NotificationsSidebarItem } from '@backstage/plugin-notifications';

/**
 * The sidebar body.
 *
 * Extracted as a named component rather than written inline in
 * `NavContentBlueprint.make({ params: { component } })` because it calls a hook
 * (`useIsDevopsAdmin`). An inline arrow is not recognised as a component, so the
 * rules-of-hooks lint cannot verify it there.
 *
 * `navItems` is typed from the package's own exported `NavContentNavItems`
 * rather than inferred out of `NavContentBlueprint.make`. The blueprint's
 * `params` is a union of a plain object and a factory callback, so indexing it
 * with `['component']` does not typecheck against the union.
 */
function SidebarBody({ navItems }: { navItems: NavContentNavItems }) {
  const { isAdmin: isDevopsAdmin } = useIsDevopsAdmin();
  const nav = navItems.withComponent(item => (
    <SidebarItem icon={() => item.icon} to={item.href} text={item.title} />
  ));

  // Skipped items
  nav.take('page:search'); // Using search modal instead
  nav.take('page:notifications'); // Using NotificationsSidebarItem manually instead

  return (
    <Sidebar>
      <SidebarLogo />
      <SidebarDivider />
      <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
        <SidebarSearchModal />
      </SidebarGroup>
      <SidebarDivider />

      <SidebarGroup label="Menu" icon={<MenuIcon />}>
        <SidebarItem icon={HomeIcon} to="/" text="Home" />
        <SidebarItem icon={CategoryIcon} to="/catalog" text="Catalog">
          <SidebarSubmenu title="Catalog">
            <SidebarSubmenuItem
              title="Services"
              to="/catalog?filters[kind]=component"
            />
            <SidebarSubmenuItem
              title="Apps"
              to="/catalog?filters[kind]=system"
            />
            <SidebarDivider />
            <SidebarSubmenuItem
              title="Teams"
              to="/catalog?filters[kind]=group&filters[type]=team"
            />
            <SidebarSubmenuItem
              title="Users"
              to="/catalog?filters[kind]=user"
            />
          </SidebarSubmenu>
        </SidebarItem>
        <WorkflowSidebarItem />
        <SidebarDivider />
        {/*
              The migration overview spans every team, so it is shown only to
              devops-admins. This is presentation only — the backend enforces
              the same check and does not trust the client.

              It must be listed literally: this component never calls
              nav.rest(), so auto-discovered nav items are dropped.
            */}
        {isDevopsAdmin && (
          <SidebarItem
            icon={GitHubIcon}
            to="/github-migration"
            text="GH Migration"
          />
        )}
      </SidebarGroup>
      <SidebarSpace />
      <SidebarDivider />
      <NotificationsSidebarItem />

      <SidebarGroup
        label="Settings"
        icon={<UserSettingsSignInAvatar />}
        to="/settings"
      >
        {nav.take('page:user-settings')}
      </SidebarGroup>
    </Sidebar>
  );
}

export const SidebarContent = NavContentBlueprint.make({
  params: {
    component: ({ navItems }) => <SidebarBody navItems={navItems} />,
  },
});
